import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Input } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import { allowEnterPress } from '../utils'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'

const persistIntegerSubmission = (props, num) => {
  try {
    const { question, id, webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    const { label, type, attributes } = question
    const { required } = attributes
    const hasValue = num === 0 || num === '0' || !!num
    const answer = hasValue
      ? [
        {
          label,
          value: num.toString(),
          question: id,
        },
      ]
      : []
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: hasValue ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    completeQuestion(submission, webSurvey.order?.[0])
  } catch (e) {
    console.warn('Integer autosave failed', { error: e })
  }
}

const completeIntegerQuestion = (props, num) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes, input_validation } = question
  const { min, max } = input_validation || {}
  const { required } = attributes

  if (num != null && num !== '' && input_validation) {
    const numericValue = Number(num)
    if (
      Number.isNaN(numericValue) ||
      (min != null && numericValue < min) ||
      (max != null && numericValue > max)
    ) {
      return false
    }
  }

  const hasValue = num === 0 || num === '0' || !!num
  const answer = hasValue
    ? [
      {
        label,
        value: num.toString(),
        question: id,
      },
    ]
    : []
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: hasValue ? 'ANSWERED' : 'SKIPPED',
    answer,
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
  return true
}

function Integer(props) {
  const {
    className,
    currentSurvey,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    forwardedRef,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
  } = props
  const [num, setNum] = useState('')
  const [error, setError] = useState('')

  const { attributes, label, input_validation } = question
  const { min, max } = input_validation || {}
  const { required, unit } = attributes
  const _className = className ? ` ${className}` : ''

  useEffect(() => {
    if (storedAnswer?.[0]?.value) {
      const storedValue = storedAnswer[0].value
      setNum(storedValue)
      if (!props.suppressAutoAdvance) {
        completeIntegerQuestion(props, storedValue)
      } else {
        persistIntegerSubmission(props, storedValue)
      }
    }
  }, [])

  const validateInput = value => {
    if (value == null || value === '' || !input_validation) return ''

    const numericValue = Number(value)
    if (Number.isNaN(numericValue)) {
      return strings?.invalid_number_error ?? 'Please enter a valid number'
    }

    if (!Number.isInteger(numericValue)) {
      return strings?.invalid_integer_error ?? 'Please enter a whole number'
    }

    if (min != null && numericValue < min) {
      return strings?.min_value_error ?? `Value must be at least ${min}`
    }

    if (max != null && numericValue > max) {
      return strings?.max_value_error ?? `Value must be at most ${max}`
    }

    return ''
  }

  const onInput = (value) => {
    setAllRequiredAnswered(false, id)
    const validationError = validateInput(value)
    setError(validationError)
    setNum(value)
    if (validationError) return
    persistIntegerSubmission(props, value)
  }

  const handleCompletion = () => {
    const validationError = validateInput(num)
    if (validationError) {
      setError(validationError)
      return false
    }
    completeIntegerQuestion(props, num)
    return true
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div
          className={`survey-question numeric${_className}`}
          ref={forwardedRef}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label)
              }}
            />
          </p>
          <div className='input-units-row'>
            <Input
              inputClassName='numeric'
              inputProps={{
                inputMode: 'numeric',
                onKeyPress: (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (allowEnterPress(required, num)) {
                      handleCompletion()
                    }
                  }
                },
                pattern: '[0-9]*',
                min: min ?? undefined,
                max: max ?? undefined
              }}
              placeholder={strings.type_in_value_placeholder}
              onChange={(value) =>
                value || value === 0
                  ? onInput(Math.round(value))
                  : onInput(value)
              }
              type='number'
              step={1}
              value={num}
              hasError={!!error}
              errorText={error}
            />
            {unit && (
              <span className='units'>
                <b>{unit}</b>
              </span>
            )}
          </div>
          {!isSibling && (
            <QuestionFooter
              completed={num === 0 || num === '0' || !!num}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={handleCompletion}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
}

const ForwardedInteger = React.forwardRef((props, ref) => (
  <Integer {...props} forwardedRef={ref} />
))

export default ForwardedInteger

Integer.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ])
}
